import { format, parseISO } from 'date-fns'
import type {
  LeaveTypeId,
  TeamCalendarDay,
  TeamCalendarMember,
  TeamCalendarPayload,
} from '@lms/shared-types'
import {
  HIDDEN_LEAVE_TYPE,
  LEAVE_TYPE_STYLE,
  leaveTypeStyle,
} from '../../lib/leave-type-palette'
import { calendarName, displayName } from '../../lib/display-name'
import { LeaveBarTooltip } from '../primitives/LeaveBarTooltip'
import { Tooltip } from '../primitives/Tooltip'

interface LeaveTeamCalendarProps {
  data: TeamCalendarPayload
  /** When true (employee viewing their own leave), teammate reasons are hidden. */
  viewerIsOwner?: boolean
  /** Border colour for the requester's own bar — set to their leave-rhythm
   *  (health-bar) ring colour so the bar under review is tied to the rhythm
   *  ring at the top. Falls back to the leave-type accent when omitted. */
  requesterBorderColor?: string
}

interface DayCol {
  iso: string
  short: string
  num: number
  isWeekend: boolean
  isToday: boolean
  isPublicHoliday: boolean
  publicHolidayName: string | null
  isInLeaveWindow: boolean
}

interface PersonBar {
  member: TeamCalendarMember
  startCol: number
  endCol: number
  hasEmergency: boolean
  hasHalfDay: boolean
}

interface BlackoutBand {
  id: string
  name: string
  startCol: number
  endCol: number
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// Teammate leaves that aren't APPROVED yet are tentative — their bars
// render with a dashed border so an approver doesn't read a pending
// request as confirmed coverage loss. APPROVED is the only "hard" state.
const PENDING_LEAVE_STATUSES = new Set([
  'pending',
  'escalated',
])

/** Cell tint for weekends + public holidays — kept in step with the calendar
 *  page's MonthGrid: a public holiday gets the pale-red danger wash (the more
 *  meaningful flag, so it wins over weekend grey), a plain weekend gets the
 *  muted weekend grey, and working days stay transparent. */
function cellBackground(day: DayCol): string {
  if (day.isPublicHoliday) return 'var(--danger-bg-3)'
  if (day.isWeekend) return 'var(--weekend-bg)'
  return 'transparent'
}

function todayIso(): string {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function packRows(bars: PersonBar[]): PersonBar[][] {
  const rows: PersonBar[][] = []
  for (const bar of bars) {
    let placed = false
    for (const row of rows) {
      const last = row[row.length - 1]
      if (last && last.endCol < bar.startCol) {
        row.push(bar)
        placed = true
        break
      }
    }
    if (!placed) rows.push([bar])
  }
  return rows
}

/**
 * Team coverage strip for the leave detail page.
 *
 * Requester's leave is always pinned to a dedicated top section (thicker
 * border) so approvers can eyeball it against the team calendar at a glance.
 * Blackout windows render as striped bands (same pattern as the /calendar
 * page) above the requester rows. Teammate reasons are surfaced in tooltips
 * only when the viewer is an approver (not the requester themselves).
 */
export function LeaveTeamCalendar({
  data,
  viewerIsOwner = false,
  requesterBorderColor,
}: LeaveTeamCalendarProps): JSX.Element {
  const today = todayIso()
  const blackouts = data.blackouts ?? []

  // ── Build day columns ──────────────────────────────────────────────────────
  const days: DayCol[] = data.days.map((d) => {
    const date = new Date(d.date + 'T00:00:00')
    const dow = date.getDay()
    const labelIndex = dow === 0 ? 6 : dow - 1
    return {
      iso: d.date,
      short: DAY_LABELS[labelIndex] ?? '',
      num: date.getDate(),
      isWeekend: d.is_weekend,
      isToday: d.date === today,
      isPublicHoliday: d.is_public_holiday,
      publicHolidayName: d.public_holiday_name,
      isInLeaveWindow: d.is_in_leave_window,
    }
  })
  const cols = days.length

  // ── Blackout band specs — clamp to visible window ──────────────────────────
  const blackoutBands: BlackoutBand[] = []
  for (const b of blackouts) {
    let startCol = -1
    let endCol = -1
    for (let ci = 0; ci < days.length; ci++) {
      const d = days[ci]!
      if (d.iso >= b.start_date && d.iso <= b.end_date) {
        if (startCol < 0) startCol = ci
        endCol = ci
      }
    }
    if (startCol < 0) continue
    blackoutBands.push({ id: b.id, name: b.name, startCol, endCol })
  }

  // ── Group per-day "out" entries into per-person contiguous bars ────────────
  // Key is (employee_id, leave_type) so a composite Annual + Unpaid pair
  // for the same person breaks into two bars at the type boundary instead
  // of merging into one giant accent-tinted band. Without this split the
  // unpaid leg silently inherits the annual color and the legend hides
  // the "unpaid" swatch (overflow composite policy, Slice 4a/4c).
  type WorkingBar = {
    member: TeamCalendarMember
    startCol: number
    endCol: number
    hasEmergency: boolean
    hasHalfDay: boolean
  }
  const _barKey = (m: TeamCalendarMember): string =>
    `${m.employee_id}::${m.leave_type}`
  const openBarByKey = new Map<string, WorkingBar>()
  const allBars: PersonBar[] = []

  for (let col = 0; col < days.length; col++) {
    const dayOut = data.days[col]?.out ?? []
    const presentKeys = new Set(dayOut.map((m) => _barKey(m)))

    for (const [key, bar] of Array.from(openBarByKey.entries())) {
      if (!presentKeys.has(key)) {
        allBars.push(bar)
        openBarByKey.delete(key)
      }
    }

    for (const m of dayOut) {
      const key = _barKey(m)
      const open = openBarByKey.get(key)
      if (open) {
        open.endCol = col
        if (m.is_emergency) open.hasEmergency = true
        if (m.half_day) open.hasHalfDay = true
      } else {
        openBarByKey.set(key, {
          member: m,
          startCol: col,
          endCol: col,
          hasEmergency: m.is_emergency,
          hasHalfDay: m.half_day,
        })
      }
    }
  }
  for (const bar of openBarByKey.values()) {
    allBars.push(bar)
  }

  allBars.sort((a, b) => a.startCol - b.startCol)

  // Pack requester and teammate rows independently so the leave under review
  // always sits in a dedicated top section above the team rows.
  const requesterRows = packRows(allBars.filter((b) => b.member.is_requester))
  const teammateRows = packRows(allBars.filter((b) => !b.member.is_requester))

  return (
    <div data-testid="leave-team-calendar">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 10,
        }}
      >
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
          {(() => {
            // Count unique employees, not bars — a composite Annual + Unpaid
            // pair is two bars for the same person. The count includes the
            // requester, so word it as the impact of this leave: future-framed
            // while the request is still pending, present once it's approved.
            // Coverage counts APPROVED absences only (pending reserves no slot),
            // PLUS the requester — the "once this is approved" body. Other
            // teammates' pending bars still render (dashed) but aren't counted,
            // matching the capacity gate and every other coverage surface.
            const uniq = new Set(
              allBars
                .filter(
                  (b) =>
                    b.member.is_requester ||
                    b.member.status == null ||
                    b.member.status === 'approved',
                )
                .map((b) => b.member.employee_id),
            ).size
            const noun = uniq === 1 ? 'person' : 'people'
            const requesterPending = allBars.some(
              (b) =>
                b.member.is_requester &&
                b.member.status != null &&
                PENDING_LEAVE_STATUSES.has(b.member.status),
            )
            const startMonth = days[0] ? format(parseISO(days[0].iso), 'dd MMM') : ''
            const impact = requesterPending
              ? `${uniq} ${noun} out of ${data.team_size} once this is approved`
              : `${uniq} ${noun} out of ${data.team_size} during this leave`
            return startMonth ? `${impact} · window of ${startMonth}` : impact
          })()}
        </span>
      </div>

      {allBars.length === 0 && blackoutBands.length === 0 ? (
        <div
          data-testid="leave-team-calendar-empty"
          style={{ fontSize: 13, color: 'var(--text-3)', padding: '8px 0' }}
        >
          Full coverage during this window. No one is out.
        </div>
      ) : (
        <>
          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)',
              overflow: 'hidden',
              background: 'var(--surface)',
              boxShadow: 'var(--shadow-sm)',
            }}
          >
          {/* Header — day labels, date numbers, PH chips */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
              borderBottom: '1px solid var(--border)',
              background: 'var(--surface-2)',
            }}
          >
            {days.map((day, i) => (
              <div
                key={day.iso}
                style={{
                  padding: '8px 4px 9px',
                  textAlign: 'center',
                  borderRight:
                    i < cols - 1 ? '1px solid var(--border)' : 'none',
                  background: cellBackground(day),
                }}
              >
                <div
                  style={{
                    fontFamily: 'var(--font-mono, inherit)',
                    fontSize: 9,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: day.isPublicHoliday ? 'var(--danger)' : 'var(--text-3)',
                    marginBottom: 2,
                  }}
                >
                  {day.short}
                </div>
                <div
                  style={{
                    fontFamily: 'var(--font-mono, inherit)',
                    fontSize: 13,
                    fontWeight: day.isToday ? 700 : 500,
                    color: day.isToday
                      ? 'var(--surface)'
                      : day.isPublicHoliday
                        ? 'var(--danger)'
                        : 'var(--text)',
                    background: day.isToday ? 'var(--accent)' : 'transparent',
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                  }}
                >
                  {day.num}
                </div>
                {day.isPublicHoliday ? (
                  <div
                    title={day.publicHolidayName ?? undefined}
                    style={{
                      marginTop: 2,
                      fontSize: 8,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      // Public holidays read bright red so they stand out as
                      // hard non-working days against the working window.
                      color: 'var(--danger)',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {day.publicHolidayName ?? 'PH'}
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          {/* Body */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {/* ── Blackout bands (above all leave rows) ─────────────────────── */}
            {blackoutBands.map((band) => (
              <BlackoutBandRow key={band.id} band={band} days={days} cols={cols} />
            ))}

            {/* ── Requester section ─────────────────────────────────────────── */}
            {requesterRows.map((row, ri) => (
              <BarRow
                key={`r-${ri}`}
                row={row}
                days={days}
                cols={cols}
                isRequesterSection
                viewerIsOwner={viewerIsOwner}
                {...(requesterBorderColor !== undefined
                  ? { requesterBorderColor }
                  : {})}
              />
            ))}

            {/* ── Teammate section ───────────────────────────────────────────── */}
            {teammateRows.map((row, ri) => (
              <BarRow
                key={`t-${ri}`}
                row={row}
                days={days}
                cols={cols}
                isRequesterSection={false}
                viewerIsOwner={viewerIsOwner}
              />
            ))}
          </div>
          </div>

          {/* Legend — sits below the grid frame so it has breathing room. */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 12,
              marginTop: 14,
              fontSize: 10,
              color: 'var(--text-3)',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div
              style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}
            >
              {blackoutBands.map((band) => (
                <span
                  key={band.id}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    color: 'var(--danger-fg)',
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 2,
                      backgroundColor: 'oklch(0.93 0.03 25)',
                      backgroundImage:
                        'repeating-linear-gradient(45deg, white 0 3px, transparent 3px 6px)',
                      border: '1px dashed oklch(0.78 0.08 25)',
                      display: 'inline-block',
                    }}
                  />
                  {band.name}
                </span>
              ))}
              {days.some((d) => d.isPublicHoliday) ? (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 3,
                      background: 'var(--danger-bg-3)',
                      border: '1px solid var(--danger-line)',
                      display: 'inline-block',
                    }}
                  />
                  public holiday
                </span>
              ) : null}
            </div>
            <span>
              {days.some((d) => d.isToday) ? 'today highlighted' : ''}
            </span>
          </div>
        </>
      )}
    </div>
  )
}

// ── BlackoutBandRow ───────────────────────────────────────────────────────────

interface BlackoutBandRowProps {
  band: BlackoutBand
  days: DayCol[]
  cols: number
}

function BlackoutBandRow({ band, days, cols }: BlackoutBandRowProps): JSX.Element {
  const span = band.endCol - band.startCol + 1
  const left = (band.startCol / cols) * 100
  const width = (span / cols) * 100

  return (
    <div style={{ position: 'relative', height: 28 }}>
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          pointerEvents: 'none',
        }}
      >
        {days.map((day, ci) => (
          <div
            key={day.iso}
            style={{
              borderRight: ci < cols - 1 ? '1px solid var(--border)' : 'none',
              borderBottom: '1px solid var(--border)',
              background: cellBackground(day),
            }}
          />
        ))}
      </div>
      <Tooltip content={band.name} placement="top">
        <div
          data-testid="leave-team-calendar-blackout"
          style={{
            position: 'absolute',
            left: `calc(${left}% + 2px)`,
            width: `calc(${width}% - 4px)`,
            top: 0,
            bottom: 0,
            backgroundColor: 'oklch(0.93 0.03 25)',
            backgroundImage:
              'repeating-linear-gradient(45deg, var(--surface) 0 6px, transparent 6px 12px)',
            border: '1px dashed oklch(0.78 0.08 25)',
            borderRadius: 4,
            fontSize: 9,
            color: 'var(--danger-fg)',
            fontWeight: 600,
            fontFamily: 'var(--font-sans, inherit)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          ⌧ {band.name}
        </div>
      </Tooltip>
    </div>
  )
}

// ── BarRow ────────────────────────────────────────────────────────────────────

interface BarRowProps {
  row: PersonBar[]
  days: DayCol[]
  cols: number
  isRequesterSection: boolean
  viewerIsOwner: boolean
  requesterBorderColor?: string
}

function BarRow({
  row,
  days,
  cols,
  isRequesterSection,
  viewerIsOwner,
  requesterBorderColor,
}: BarRowProps): JSX.Element {
  return (
    <div style={{ position: 'relative', height: 40 }}>
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          pointerEvents: 'none',
        }}
      >
        {days.map((day, ci) => (
          <div
            key={day.iso}
            style={{
              borderRight: ci < cols - 1 ? '1px solid var(--border)' : 'none',
              borderBottom: '1px solid var(--border)',
              background: cellBackground(day),
            }}
          />
        ))}
      </div>

      {row.map((bar) => {
        const span = bar.endCol - bar.startCol + 1
        const left = (bar.startCol / cols) * 100
        const width = (span / cols) * 100
        const accent = leaveAccent(bar.member.leave_type)
        // A not-yet-approved leave is tentative coverage loss → dashed border,
        // matching the "pending approval" legend. This applies to the
        // requester's OWN bar too: a pending leave under review must not read as
        // confirmed just because it's the subject. Only APPROVED is solid.
        const isTentative =
          bar.member.status != null &&
          PENDING_LEAVE_STATUSES.has(bar.member.status)
        const barBorderStyle = isTentative ? 'dashed' : 'solid'
        // The requester's bar border follows their leave-rhythm (health-bar)
        // ring colour when provided, tying it to the rhythm ring at the top;
        // teammates keep the leave-type accent.
        const barBorderColor =
          bar.member.is_requester && requesterBorderColor
            ? requesterBorderColor
            : accent.swatch
        // Tooltip surfaces the *full* leave range (e.g. 98-day maternity leave)
        // even when the visible window clips the bar to a shorter slice — using
        // the clipped iso would mis-report the duration.
        const barStartIso = bar.member.leave_start_date
        const barEndIso = bar.member.leave_end_date
        const showReason = !viewerIsOwner && !bar.member.is_requester
        return (
          <Tooltip
            key={`${bar.member.employee_id}-${bar.startCol}`}
            content={
              <LeaveBarTooltip
                employeeName={displayName({
                  name: bar.member.name,
                  preferredName: bar.member.preferred_name,
                })}
                leaveType={bar.member.leave_type}
                {...(barStartIso !== undefined ? { startDate: barStartIso } : {})}
                {...(barEndIso !== undefined ? { endDate: barEndIso } : {})}
                halfDay={bar.hasHalfDay}
                isEmergency={bar.hasEmergency}
                isRequester={bar.member.is_requester}
                {...(showReason &&
                bar.member.reason !== undefined &&
                bar.member.reason !== null
                  ? { reason: bar.member.reason }
                  : {})}
              />
            }
            placement="top"
          >
            <div
              data-testid="leave-team-calendar-bar"
              data-is-requester={bar.member.is_requester ? 'true' : 'false'}
              style={{
                position: 'absolute',
                left: `calc(${left}% + 2px)`,
                width: `calc(${width}% - 4px)`,
                // Row is 40px tall for breathing room, but the badge itself
                // stays its original ~26px height — extra space becomes margin,
                // not a fatter pill. (40 − 26) / 2 = 7px inset.
                top: 7,
                bottom: 7,
                // Light pastel of the canonical `swatch` hue — the swatch mixed
                // into the surface (opaque, so it never bleeds through the grid)
                // at a low ratio, giving a soft tint of each leave colour that
                // the dark same-hue ink still reads crisply on.
                background: `color-mix(in oklch, ${accent.swatch} 28%, var(--surface))`,
                border: `${isRequesterSection ? 1.5 : 1}px ${barBorderStyle} ${barBorderColor}`,
                borderRadius: 999,
                boxShadow: '0 1px 3px rgba(32, 41, 69, 0.14)',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                paddingLeft: 6,
                paddingRight: 8,
                fontSize: 11,
                // Dark same-hue ink reads crisply on the light translucent
                // fill (white went low-contrast once the fill lightened).
                color: accent.ink,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              <span style={{ fontWeight: 600, letterSpacing: '0.01em' }}>
                {calendarName({
                  name: bar.member.name,
                  preferredName: bar.member.preferred_name,
                })}
              </span>
              <span style={{ opacity: 0.9 }}>
                {bar.member.leave_type === HIDDEN_LEAVE_TYPE
                  ? leaveTypeStyle(HIDDEN_LEAVE_TYPE).label
                  : bar.member.leave_type}
              </span>
              {bar.hasEmergency ? (
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: 9,
                    fontWeight: 700,
                    color: accent.ink,
                  }}
                >
                  ⚠
                </span>
              ) : null}
            </div>
          </Tooltip>
        )
      })}
    </div>
  )
}

// ── leaveAccent ───────────────────────────────────────────────────────────────

/** Per-leave-type colours from the canonical LEAVE_TYPE_STYLE palette — the
 *  same source the home page, calendars, and legends use, so the coverage
 *  bars stay consistent app-wide. `bg`/`line`/`ink` map straight onto the bar
 *  background / border / text. */
function leaveAccent(type: string): {
  bg: string
  line: string
  ink: string
  swatch: string
} {
  // A teammate's type the viewer may not see arrives as the `'hidden'`
  // sentinel (#263) and renders neutral grey, never as a guessed type.
  const style =
    type === HIDDEN_LEAVE_TYPE
      ? leaveTypeStyle(HIDDEN_LEAVE_TYPE)
      : (LEAVE_TYPE_STYLE[type as LeaveTypeId] ?? LEAVE_TYPE_STYLE.annual)
  // Same shades as the original leave bars (LeaveBar primitive / homepage
  // calendars): the canonical palette bg/line/ink, verbatim. `swatch` is the
  // saturated legend-dot hue (matches the home page CalendarLegend).
  return { bg: style.bg, line: style.line, ink: style.ink, swatch: style.swatch }
}
